import {
  Box3,
  DirectionalLight,
  Group,
  HemisphereLight,
  Object3D,
  Plane,
  Raycaster,
  Vector2,
  Vector3,
  type Camera
} from "three";

import { WebArError } from "./contracts.js";
import type {
  WebArRuntimePort,
  WebArRuntimeSession
} from "./controller.js";
import type { Xr8PipelineModule, Xr8Runtime, Xr8SceneModel } from "./xr8-types.js";

interface PlacementControls {
  setScale(scale: number): number;
  placeAhead(reason: "initial" | "recenter"): void;
  dispose(): void;
}

function asXr8Runtime(value: unknown): Xr8Runtime {
  const runtime = value as Partial<Xr8Runtime> | null;
  if (
    !runtime ||
    typeof runtime.run !== "function" ||
    typeof runtime.stop !== "function" ||
    typeof runtime.addCameraPipelineModules !== "function" ||
    typeof runtime.removeCameraPipelineModules !== "function" ||
    !runtime.GlTextureRenderer ||
    !runtime.Threejs ||
    !runtime.XrController ||
    !runtime.XrConfig
  ) {
    throw new WebArError("runtime_failed", "The XR8 runtime is invalid.", false);
  }
  return runtime as Xr8Runtime;
}

function normalizeModel(model: Object3D): void {
  const box = new Box3().setFromObject(model);
  if (box.isEmpty()) {
    throw new WebArError("model_load_failed", "The GLB does not contain visible geometry.", false);
  }
  const center = box.getCenter(new Vector3());
  model.position.add(new Vector3(-center.x, -box.min.y, -center.z));
}

function quantizeScale(
  value: number,
  scale: { readonly minimum: number; readonly maximum: number; readonly step: number }
): number {
  const clamped = Math.min(scale.maximum, Math.max(scale.minimum, value));
  const steps = Math.round((clamped - scale.minimum) / scale.step);
  return Number(Math.min(scale.maximum, scale.minimum + steps * scale.step).toFixed(6));
}

function createPlacementControls(input: {
  readonly canvas: HTMLCanvasElement;
  readonly camera: Camera;
  readonly anchor: Group;
  readonly scale: {
    readonly minimum: number;
    readonly maximum: number;
    readonly initial: number;
    readonly step: number;
  };
  readonly initialDistanceMeters: number;
  readonly onPlacement: (reason: "initial" | "tap" | "recenter") => void;
  readonly onScale: (scale: number) => void;
}): PlacementControls {
  const pointers = new Map<number, { readonly x: number; readonly y: number }>();
  const raycaster = new Raycaster();
  const floor = new Plane(new Vector3(0, 1, 0), 0);
  const intersection = new Vector3();
  let currentScale = quantizeScale(input.scale.initial, input.scale);
  let pinchDistance: number | undefined;
  let pinchScale = currentScale;
  let moved = false;
  input.anchor.scale.setScalar(currentScale);

  const setScale = (value: number): number => {
    currentScale = quantizeScale(value, input.scale);
    input.anchor.scale.setScalar(currentScale);
    input.onScale(currentScale);
    return currentScale;
  };

  const placeAhead = (reason: "initial" | "recenter") => {
    const forward = new Vector3();
    input.camera.getWorldDirection(forward);
    forward.y = 0;
    if (forward.lengthSq() < 0.0001) forward.set(0, 0, -1);
    forward.normalize();
    input.anchor.position.copy(input.camera.position)
      .addScaledVector(forward, input.initialDistanceMeters);
    input.anchor.position.y = 0;
    input.anchor.visible = true;
    input.onPlacement(reason);
  };

  const positionFromPointer = (event: PointerEvent) => {
    const bounds = input.canvas.getBoundingClientRect();
    if (bounds.width <= 0 || bounds.height <= 0) return;
    const point = new Vector2(
      ((event.clientX - bounds.left) / bounds.width) * 2 - 1,
      -((event.clientY - bounds.top) / bounds.height) * 2 + 1
    );
    raycaster.setFromCamera(point, input.camera);
    if (raycaster.ray.intersectPlane(floor, intersection)) {
      input.anchor.position.copy(intersection);
      input.anchor.visible = true;
      input.onPlacement("tap");
    }
  };

  const pointerDown = (event: PointerEvent) => {
    input.canvas.setPointerCapture(event.pointerId);
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    moved = false;
    if (pointers.size === 2) {
      const [first, second] = [...pointers.values()];
      if (first && second) {
        pinchDistance = Math.hypot(first.x - second.x, first.y - second.y);
        pinchScale = currentScale;
      }
    }
  };
  const pointerMove = (event: PointerEvent) => {
    const previous = pointers.get(event.pointerId);
    if (!previous) return;
    if (Math.hypot(previous.x - event.clientX, previous.y - event.clientY) > 4) moved = true;
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.size !== 2 || !pinchDistance || pinchDistance <= 0) return;
    const [first, second] = [...pointers.values()];
    if (!first || !second) return;
    const nextDistance = Math.hypot(first.x - second.x, first.y - second.y);
    setScale(pinchScale * (nextDistance / pinchDistance));
    event.preventDefault();
  };
  const pointerEnd = (event: PointerEvent) => {
    const wasSinglePointer = pointers.size === 1;
    pointers.delete(event.pointerId);
    if (wasSinglePointer && !moved && event.type === "pointerup") positionFromPointer(event);
    if (pointers.size < 2) pinchDistance = undefined;
  };
  input.canvas.addEventListener("pointerdown", pointerDown);
  input.canvas.addEventListener("pointermove", pointerMove);
  input.canvas.addEventListener("pointerup", pointerEnd);
  input.canvas.addEventListener("pointercancel", pointerEnd);

  return {
    setScale,
    placeAhead,
    dispose() {
      pointers.clear();
      input.canvas.removeEventListener("pointerdown", pointerDown);
      input.canvas.removeEventListener("pointermove", pointerMove);
      input.canvas.removeEventListener("pointerup", pointerEnd);
      input.canvas.removeEventListener("pointercancel", pointerEnd);
    }
  };
}

export function createXr8ThreeRuntime(): WebArRuntimePort {
  return {
    async start(input): Promise<WebArRuntimeSession> {
      if (input.signal.aborted) {
        throw new WebArError("cancelled", "AR startup was cancelled.", true);
      }
      const xr8 = asXr8Runtime(input.engine.runtime);
      const resource = input.model.resource as Partial<Xr8SceneModel> | null;
      if (!resource || !(resource.scene instanceof Object3D)) {
        throw new WebArError("model_load_failed", "The decoded GLB scene is invalid.", false);
      }
      normalizeModel(resource.scene);

      const world = new Group();
      const anchor = new Group();
      anchor.visible = false;
      anchor.add(resource.scene);
      world.add(anchor);
      world.add(new HemisphereLight(0xffffff, 0x253040, 1.5));
      const keyLight = new DirectionalLight(0xffffff, 2);
      keyLight.position.set(2, 4, 1);
      world.add(keyLight);

      const moduleName = `customiznator-world-${input.launch.launchGrantId}`;
      let controls: PlacementControls | undefined;
      let attachedScene: Group["parent"];
      let stopped = false;
      let settled = false;
      let resolveStarted!: () => void;
      let rejectStarted!: (error: unknown) => void;
      const started = new Promise<void>((resolve, reject) => {
        resolveStarted = resolve;
        rejectStarted = reject;
      });
      const startupTimeout = globalThis.setTimeout(() => {
        fail(new WebArError("runtime_failed", "The AR camera pipeline timed out.", true));
      }, 20_000);

      const teardown = () => {
        if (stopped) return;
        stopped = true;
        globalThis.clearTimeout(startupTimeout);
        input.signal.removeEventListener("abort", abort);
        controls?.dispose();
        controls = undefined;
        attachedScene?.remove(world);
        anchor.remove(resource.scene!);
        try { xr8.stop(); } catch {}
        try { xr8.removeCameraPipelineModules(modules); } catch {}
      };
      const fail = (cause: unknown) => {
        if (stopped) return;
        const error = cause instanceof WebArError
          ? cause
          : new WebArError("runtime_failed", "The AR runtime failed.", true, { cause });
        if (!settled) {
          settled = true;
          teardown();
          rejectStarted(error);
        } else {
          input.onFailure(error);
        }
      };
      const abort = () => fail(new WebArError("cancelled", "AR startup was cancelled.", true));

      const placementModule: Xr8PipelineModule = {
        name: moduleName,
        onStart() {
          try {
            const xrScene = xr8.Threejs.xrScene();
            attachedScene = xrScene.scene;
            xrScene.scene.add(world);
            controls = createPlacementControls({
              canvas: input.canvas,
              camera: xrScene.camera,
              anchor,
              scale: input.launch.scale,
              initialDistanceMeters: input.launch.placement.initialDistanceMeters,
              onPlacement: input.onPlacement,
              onScale: input.onScale
            });
            if (!settled) {
              settled = true;
              globalThis.clearTimeout(startupTimeout);
              resolveStarted();
            }
          } catch (cause) {
            fail(cause);
          }
        },
        onUpdate({ processCpuResult }) {
          if (
            controls &&
            !anchor.visible &&
            processCpuResult?.reality?.trackingStatus === "NORMAL"
          ) {
            controls.placeAhead("initial");
          }
        },
        onCameraStatusChange({ status }) {
          if (status === "failed") {
            fail(new WebArError("camera_denied", "XR8 could not start the camera.", true));
          }
        },
        onException: fail,
        onDetach() {
          controls?.dispose();
        }
      };
      const modules = [
        xr8.GlTextureRenderer.pipelineModule(),
        xr8.Threejs.pipelineModule(),
        xr8.XrController.pipelineModule(),
        placementModule
      ] as const;

      input.signal.addEventListener("abort", abort, { once: true });
      try {
        xr8.XrController.configure({
          disableWorldTracking: false,
          enableLighting: true,
          enableWorldPoints: false,
          scale: "absolute"
        });
        xr8.addCameraPipelineModules(modules);
        xr8.run({
          canvas: input.canvas,
          allowedDevices: xr8.XrConfig.device().MOBILE_AND_HEADSETS,
          cameraConfig: { direction: xr8.XrConfig.camera().BACK },
          glContextConfig: {
            alpha: false,
            antialias: true,
            preserveDrawingBuffer: false,
            powerPreference: "high-performance"
          }
        });
      } catch (cause) {
        fail(cause);
      }

      await started;
      return {
        setScale(value) {
          return controls?.setScale(value) ?? input.launch.scale.initial;
        },
        reposition() {
          controls?.placeAhead("recenter");
        },
        stop: teardown
      };
    }
  };
}
